import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Alert,
  Modal,
  TextInput,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { RootStackParamList } from '../../types/navigation';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import ResponsiveContainer from '../../components/shared/ResponsiveContainer';
import Button from '../../components/common/Button';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '../../hooks/useAuth';
import { backendService } from '../../services/BackendService';
import { palette } from '../../utils/palette';
import {
  getToken,
  savePendingOnboarding,
  clearPendingOnboarding,
} from '../../utils/storage';

type OnboardingScreenNavigationProp =
  NativeStackNavigationProp<RootStackParamList>;

// Age brackets
const AGE_BRACKETS = [
  { id: 'under-18', label: 'Under 18' },
  { id: '18-25', label: '18-25' },
  { id: '26-35', label: '26-35' },
  { id: '36-45', label: '36-45' },
  { id: '46-55', label: '46-55' },
  { id: '56-65', label: '56-65' },
  { id: '65+', label: '65+' },
];

// Industries
const INDUSTRIES = [
  { id: 'technology', label: 'Tech', icon: 'laptop-outline' },
  { id: 'healthcare', label: 'Healthcare', icon: 'medical-outline' },
  { id: 'finance', label: 'Finance', icon: 'card-outline' },
  { id: 'education', label: 'Education', icon: 'school-outline' },
  { id: 'retail', label: 'Retail', icon: 'storefront-outline' },
  { id: 'marketing', label: 'Marketing', icon: 'megaphone-outline' },
  { id: 'arts', label: 'Arts & Design', icon: 'brush-outline' },
  { id: 'hospitality', label: 'Hospitality', icon: 'restaurant-outline' },
  { id: 'consulting', label: 'Consulting', icon: 'people-outline' },
  { id: 'other', label: 'Other', icon: 'ellipsis-horizontal-outline' },
];

interface UserProfile {
  displayName: string;
  ageBracket: string;
  industry: string;
}

const OnboardingScreen = () => {
  const navigation = useNavigation<OnboardingScreenNavigationProp>();
  const insets = useSafeAreaInsets();
  const { isMobile } = useResponsiveLayout();
  const {
    signOut,
    refreshUser,
    setPendingEmailVerification,
    updateOnboardingStatus,
    token,
    user,
  } = useAuth();

  const [profile, setProfile] = useState<UserProfile>({
    displayName: '',
    ageBracket: '',
    industry: '',
  });

  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [displayNameError, setDisplayNameError] = useState<string | null>(null);

  const handleDisplayNameChange = (displayName: string) => {
    setProfile((prev) => ({ ...prev, displayName }));
    // Clear error when user starts typing
    if (displayNameError) {
      setDisplayNameError(null);
    }
  };

  const handleAgeSelect = (ageBracket: string) => {
    setProfile((prev) => ({ ...prev, ageBracket }));
  };

  const handleIndustrySelect = (industry: string) => {
    setProfile((prev) => ({ ...prev, industry }));
  };

  const handleNext = () => {
    if (currentStep === 0 && !profile.displayName.trim()) {
      Alert.alert('Required', 'Please enter your display name to continue.');
      return;
    }

    if (currentStep === 1 && !profile.ageBracket) {
      Alert.alert('Required', 'Please select your age bracket to continue.');
      return;
    }

    if (currentStep === 2 && !profile.industry) {
      Alert.alert('Required', 'Please select your industry to continue.');
      return;
    }

    if (currentStep === 0) {
      setCurrentStep(1);
    } else if (currentStep === 1) {
      setCurrentStep(2);
    } else if (currentStep === 2) {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderUsernameInput = () => (
    <View style={styles.stepContainer}>
      <Text style={styles.stepTitle}>What should we call you?</Text>
      <Text style={styles.stepSubtitle}>
        Enter a display name to personalize your experience
      </Text>

      <View style={styles.inputContainer}>
        <TextInput
          style={[styles.textInput, displayNameError && styles.textInputError]}
          placeholder="Enter your display name"
          value={profile.displayName}
          onChangeText={handleDisplayNameChange}
          autoCapitalize="words"
          autoCorrect={false}
        />
        {displayNameError && (
          <Text style={styles.errorText}>{displayNameError}</Text>
        )}
      </View>
    </View>
  );

  const handleBackToSignIn = () => {
    const hasDisplayName =
      profile.displayName && profile.displayName.trim() !== '';
    const hasAgeBracket =
      profile.ageBracket && profile.ageBracket.trim() !== '';
    const hasIndustry = profile.industry && profile.industry.trim() !== '';
    const hasAnyProgress = hasDisplayName || hasAgeBracket || hasIndustry;

    if (hasAnyProgress) {
      // Show confirmation dialog only if progress was made
      // Alert.alert first, with fallback to custom modal
      setTimeout(() => {
        try {
          Alert.alert(
            'Go Back to Sign In',
            'Are you sure you want to go back to the sign in page? Your onboarding progress will be lost.',
            [
              {
                text: 'Cancel',
                style: 'cancel',
                onPress: () => {},
              },
              {
                text: 'Go Back',
                style: 'destructive',
                onPress: () => {
                  handleSignOutAndNavigate();
                },
              },
            ],
            {
              cancelable: true,
              onDismiss: () => {},
            }
          );

          // Fallback: if alert doesn't show within 1 second, show custom modal
          setTimeout(() => {
            setShowConfirmModal(true);
          }, 1000);
        } catch {
          setShowConfirmModal(true);
        }
      }, 50); // Small delay to ensure proper rendering
    } else {
      handleSignOutAndNavigate();
    }
  };

  const handleSignOutAndNavigate = async () => {
    setIsSigningOut(true);

    try {
      // Sign out the user
      await signOut();

      // Force navigation to Login screen with a more reliable approach
      setTimeout(() => {
        try {
          navigation.reset({
            index: 0,
            routes: [{ name: 'Login' }],
          });
        } catch {
          // Fallback: try to navigate directly
          navigation.navigate('Login');
        }
      }, 100);
    } catch {
      Alert.alert(
        'Error',
        'Failed to sign out. Please try again or complete the onboarding process.',
        [{ text: 'OK' }]
      );
    } finally {
      setIsSigningOut(false);
    }
  };

  const handleComplete = async () => {
    setIsLoading(true);
    setDisplayNameError(null);

    // Validate display name is provided
    if (!profile.displayName.trim()) {
      setDisplayNameError('Display name is required.');
      setIsLoading(false);
      return;
    }

    try {
      // Save onboarding data to backend
      const onboardingData = {
        name: profile.displayName.trim(),
        age_bracket: profile.ageBracket,
        industry: profile.industry,
      };

      // Save to local storage - will be submitted on sign-in if background save fails
      await savePendingOnboarding(onboardingData);

      const accessToken = token ?? (await getToken('auth_token'));

      if (user?.email_verified) {
        // Email already confirmed – skip the verify-email screen entirely.
        // Optimistically mark onboarding complete so the navigator switches to
        // the MainTabs group before we call reset().
        updateOnboardingStatus(true);
        navigation.reset({
          index: 0,
          routes: [{ name: 'MainTabs', params: { section: 'home' } }],
        });
      } else {
        // Email not yet verified – show the verification prompt first.
        setPendingEmailVerification(true);
        navigation.reset({
          index: 0,
          routes: [{ name: 'VerifyEmailRequired' }],
        });
      }

      // Complete onboarding via backend in background (save name, age, industry)
      if (accessToken) {
        backendService.onboarding
          .complete(onboardingData, accessToken)
          .then(() => {
            refreshUser();
            clearPendingOnboarding(); // Success - no need to retry on sign-in
          })
          .catch(() => {
            /* Left in storage - will complete on sign-in with fresh token */
          });
      }
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { detail?: string } };
        message?: string;
      };
      const errorMessage =
        err?.response?.data?.detail ||
        err?.message ||
        'Failed to complete onboarding. Please try again.';
      Alert.alert('Error', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const renderAgeSelection = () => (
    <View style={styles.stepContainer}>
      <Text style={styles.stepTitle}>What&apos;s your age range?</Text>
      <Text style={styles.stepSubtitle}>
        This helps us personalize your experience
      </Text>

      <View style={styles.optionsContainer}>
        {AGE_BRACKETS.map((age) => (
          <TouchableOpacity
            key={age.id}
            style={[
              styles.optionCard,
              profile.ageBracket === age.id && styles.selectedOptionCard,
            ]}
            onPress={() => handleAgeSelect(age.id)}
          >
            <Text
              style={[
                styles.optionText,
                profile.ageBracket === age.id && styles.selectedOptionText,
              ]}
            >
              {age.label}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );

  const renderIndustrySelection = () => (
    <View style={styles.stepContainer}>
      <Text style={styles.stepTitle}>What industry do you work in?</Text>
      <Text style={styles.stepSubtitle}>
        We&apos;ll suggest colors that work best for your professional
        environment
      </Text>

      <View style={styles.optionsContainer}>
        {INDUSTRIES.map((industry) => (
          <TouchableOpacity
            key={industry.id}
            style={[
              styles.industryCard,
              profile.industry === industry.id && styles.selectedOptionCard,
            ]}
            onPress={() => handleIndustrySelect(industry.id)}
          >
            <Ionicons
              name={
                industry.icon as React.ComponentProps<typeof Ionicons>['name']
              }
              size={20}
              color={
                profile.industry === industry.id
                  ? palette.brightViolet
                  : palette.darkGrayBlue
              }
            />
            <Text
              style={[
                styles.industryText,
                profile.industry === industry.id && styles.selectedOptionText,
              ]}
            >
              {industry.label}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return renderUsernameInput();
      case 1:
        return renderAgeSelection();
      case 2:
        return renderIndustrySelection();
      default:
        return renderUsernameInput();
    }
  };

  return (
    <ResponsiveContainer style={styles.container}>
      {/* Back to Sign Up Button - Outside content area */}
      <View
        style={[
          styles.topLeftContainer,
          { top: isMobile ? insets.top + 15 : insets.top + 10 },
        ]}
      >
        <TouchableOpacity
          style={[
            styles.backToSignUpButton,
            isSigningOut && styles.backToSignUpButtonDisabled,
          ]}
          onPress={() => {
            if (!isSigningOut) {
              handleBackToSignIn();
            }
          }}
          disabled={isSigningOut}
        >
          {isSigningOut ? (
            <Ionicons
              name="hourglass-outline"
              size={20}
              color={palette.mediumGray}
            />
          ) : (
            <Ionicons
              name="arrow-back"
              size={20}
              color={palette.darkGrayBlue}
            />
          )}
          <Text
            style={[
              styles.backToSignUpText,
              isSigningOut && styles.backToSignUpTextDisabled,
            ]}
          >
            {isSigningOut ? 'Signing out...' : 'Back to Sign In'}
          </Text>
        </TouchableOpacity>
      </View>

      <View
        style={[
          styles.contentContainer,
          { paddingTop: isMobile ? insets.top + 80 : insets.top + 60 },
          !isMobile && styles.webContentContainer,
        ]}
      >
        {/* Header */}
        <View style={[styles.header, isMobile && styles.headerMobile]}>
          <Text style={styles.title}>Tell us about yourself</Text>
          <Text style={styles.subtitle}>
            This helps us create a personalized color experience just for you
          </Text>
        </View>

        {/* Progress indicator */}
        <View style={styles.progressContainer}>
          {[0, 1, 2].map((step) => (
            <View
              key={step}
              style={[
                styles.progressDot,
                step <= currentStep && styles.progressDotActive,
              ]}
            />
          ))}
        </View>

        {/* Current step content */}
        {renderCurrentStep()}

        {/* Navigation buttons */}
        <View style={styles.navigationContainer}>
          {currentStep > 0 && (
            <TouchableOpacity style={styles.backButton} onPress={handleBack}>
              <Ionicons
                name="chevron-back"
                size={24}
                color={palette.darkGrayBlue}
              />
              <Text style={styles.backButtonText}>Back</Text>
            </TouchableOpacity>
          )}

          <View style={styles.nextButtonContainer}>
            <Button
              title={currentStep === 2 ? 'Complete Setup' : 'Next'}
              onPress={handleNext}
              loading={isLoading}
              disabled={currentStep === 0 && !profile.displayName.trim()}
              style={styles.nextButton}
              trackClick={true}
              trackingLocation="onboarding-screen"
              trackingData={{
                button_name: currentStep === 2 ? 'Complete Setup' : 'Next',
                button_type: 'next',
                screen: 'onboarding',
              }}
            />
          </View>
        </View>
      </View>

      {/* Custom Confirmation Modal */}
      <Modal
        visible={showConfirmModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowConfirmModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Go Back to Sign In</Text>
            <Text style={styles.modalMessage}>
              Are you sure you want to go back to the sign in page? Your
              onboarding progress will be lost.
            </Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={[styles.modalButton, styles.modalCancelButton]}
                onPress={() => {
                  setShowConfirmModal(false);
                }}
              >
                <Text style={styles.modalCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.modalConfirmButton]}
                onPress={() => {
                  setShowConfirmModal(false);
                  handleSignOutAndNavigate();
                }}
              >
                <Text style={styles.modalConfirmText}>Go Back</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </ResponsiveContainer>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: palette.white,
  },
  contentContainer: {
    flex: 1,
    paddingHorizontal: 24,
    paddingBottom: 20,
    justifyContent: 'space-between',
  },
  webContentContainer: {
    marginTop: 32,
    width: '50%',
    alignSelf: 'center',
  },
  topLeftContainer: {
    position: 'absolute',
    left: 24,
    zIndex: 10,
  },
  backToSignUpButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderWidth: 1,
    borderColor: palette.lightGray,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  backToSignUpText: {
    fontSize: 14,
    color: palette.darkGray,
    marginLeft: 6,
    fontWeight: '600',
  },
  backToSignUpButtonDisabled: {
    backgroundColor: 'rgba(255, 255, 255, 0.6)',
    borderColor: palette.lightGray,
    opacity: 0.7,
  },
  backToSignUpTextDisabled: {
    color: palette.mediumGray,
  },
  header: {
    marginBottom: 30,
    alignItems: 'center',
  },
  headerMobile: {
    marginTop: 10,
    marginBottom: 20,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: palette.veryDarkBlue,
    textAlign: 'center',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    color: palette.darkGrayBlue,
    textAlign: 'center',
    lineHeight: 24,
  },
  progressContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginBottom: 24,
    gap: 12,
  },
  progressDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: palette.lightGray,
  },
  progressDotActive: {
    backgroundColor: palette.brightViolet,
  },
  stepContainer: {
    flex: 1,
    marginTop: 10,
    marginBottom: 20,
    justifyContent: 'center',
  },
  stepTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: palette.veryDarkBlue,
    textAlign: 'center',
    marginTop: 8,
    marginBottom: 6,
  },
  stepSubtitle: {
    fontSize: 15,
    color: palette.darkGrayBlue,
    textAlign: 'center',
    marginBottom: 16,
    lineHeight: 22,
  },
  inputContainer: {
    width: '100%',
    marginTop: 20,
    alignItems: 'center',
  },
  textInput: {
    width: '100%',
    maxWidth: 400,
    backgroundColor: palette.softGray,
    borderWidth: 2,
    borderColor: palette.lightGray,
    borderRadius: 12,
    paddingVertical: 16,
    paddingHorizontal: 20,
    fontSize: 16,
    color: palette.veryDarkBlue,
    textAlign: 'center',
  },
  textInputError: {
    borderColor: palette.brightRed,
    backgroundColor: palette.lightRed,
  },
  errorText: {
    marginTop: 8,
    fontSize: 14,
    color: palette.brightRed,
    textAlign: 'center',
    fontWeight: '500',
  },
  optionsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 6,
  },
  optionCard: {
    backgroundColor: palette.softGray,
    borderWidth: 2,
    borderColor: palette.lightGray,
    borderRadius: 12,
    paddingVertical: 16,
    paddingHorizontal: 24,
    alignItems: 'center',
    minWidth: 100,
  },
  selectedOptionCard: {
    backgroundColor: palette.lightGrayViolet,
    borderColor: palette.brightViolet,
  },
  optionText: {
    fontSize: 16,
    fontWeight: '500',
    color: palette.mediumGray2,
  },
  selectedOptionText: {
    color: palette.brightViolet,
  },
  industryCard: {
    backgroundColor: palette.softGray,
    borderWidth: 2,
    borderColor: palette.lightGray,
    borderRadius: 10,
    paddingVertical: 6,
    paddingHorizontal: 10,
    alignItems: 'center',
    width: '20%',
    minHeight: 60,
    justifyContent: 'center',
    gap: 3,
  },
  industryText: {
    fontSize: 12,
    fontWeight: '500',
    color: palette.mediumGray2,
    textAlign: 'center',
  },
  navigationContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 'auto',
    paddingTop: 20,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    gap: 4,
  },
  backButtonText: {
    fontSize: 16,
    color: palette.darkGrayBlue,
    fontWeight: '500',
  },
  nextButtonContainer: {
    flex: 1,
    marginLeft: 16,
  },
  nextButton: {
    marginBottom: 0,
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: palette.white,
    borderRadius: 12,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: palette.veryDarkBlue,
    marginBottom: 12,
    textAlign: 'center',
  },
  modalMessage: {
    fontSize: 16,
    color: palette.darkGrayBlue,
    lineHeight: 24,
    marginBottom: 24,
    textAlign: 'center',
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  },
  modalButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: 'center',
  },
  modalCancelButton: {
    backgroundColor: palette.veryLightGray,
    borderWidth: 1,
    borderColor: palette.lightGray,
  },
  modalConfirmButton: {
    backgroundColor: palette.brightRed,
  },
  modalCancelText: {
    fontSize: 16,
    fontWeight: '600',
    color: palette.darkGray,
  },
  modalConfirmText: {
    fontSize: 16,
    fontWeight: '600',
    color: palette.white,
  },
});

export default OnboardingScreen;
